<template>
  <div v-loading="!chartMainData">
    <div v-if="chartMainData" class="chart-template">
      <div class="chart-template__header">
        <div class="chart-template__header-title">{{ chartTitle }}</div>
      </div>
      <div class="chart-template__wrapper">
        <div class="chart-template__info">
          <div v-if="radioButtonOptions" class="chart-template__stats-filter">
            <RadioButtonChartTemplate
              :options="radioButtonOptions"
              @updateOption="updateRadioButtonOptions"
            />
          </div>
          <div class="chart-template__list">
            <div
              v-for="item in getVisibleItems"
              :key="item.id"
              class="chart-template__item"
            >
              <div class="chart-template__item-title"> 
                <div
                  class="chart-template__item-circle"
                  :style="{backgroundColor: item.fillColor}"
                ></div>
                <div class="chart-template__item-title-text">{{ item.listItemTitle }}</div>
              </div>
              <div class="chart-template__item-count"> {{ item.listTotalItemCount.toString().replace(/(?!^)(?=(?:\d{3})+(?:\.|$))/gm, ' ') }}</div>
              <div class="chart-template__item-percent">{{ item.persent }}%</div>
            </div>
          </div>
          <div
            v-if="isHideShowAllBtn"
            class="chart-template__show-all"
            @click="toggleShowAll"
          >
            <span class="chart-template__show-all-text">{{ showAll ? "Свернуть" : "Показать всe" }}</span> 
            <span 
              class="chart-template__show-all-icon"
              :class="{'chart-template__show-all-icon_rotate': showAll }"
            ></span>
          </div>
        </div>
        <div class="chart-template__chart-wrapper" :style="{marginTop: chartMarginTopPosition}">
          <VueApexCharts :height="chartHeight" :width="chartWidth" :series="getSeries" :options="getOptions"  type="donut"/>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import VueApexCharts from 'vue-apexcharts';
import ModelSelect from '@/components/UI/models/ModelSelect';
import RadioButtonChartTemplate from './RadioButtonChartTemplate';
import {CHART_MONTHS, CHART_DEFAULT_COLORS, getDefaultDonutOptions } from '../chartHelper';

export default {
  name: "DonutChartTemplate",
  components: {
    RadioButtonChartTemplate,
    VueApexCharts,
  },
  props: {
    chartTitle: {
      type: String,
    },
    chartMainData: {
      type: Object,
    },
    chartSeriesYKey: {
      type: String,
    },
    chartHeight: {
      type: Number,
    },
    chartWidth: {
      type: Number,
    },
    chartOptionsFuncParams: {
      type: Object,
    },
    radioButtonOptions: {
      type: Array,
      default: null,
    },
    keyNamesForChartMainData: {
      type: Object,
    },
    currentStaticValue: {
      type: String,
    },
    genderDistribution: {
      type: Boolean,
      default: false,
    },
    chartMarginTopPosition: {
      type: String,
    },
  },
  data() {
    return {
      year: new Date().getFullYear(),
      currentRadioButtonValue: null,
      totalCountBySelectedRadio: null,
      totalStaticCount: null,
      visibleCount: 9,
      donutLabels: [],
      showAll: false,
    };
  },
  watch: {
    year(value) {
      this.$emit('year-update', value);
    },
  },
  computed: {
    getVisibleItems() {
      return this.showAll ? this.getCurrentChartMainData : this.getCurrentChartMainData.slice(0, this.visibleCount);
    },
    getCurrentChartMainData() {
      if(Object.keys(this.chartMainData).length === 0) return;
      const currentValue = this.currentRadioButtonValue || this.currentStaticValue;

      const totalCount = this.chartMainData[currentValue].reduce((sum, item) => {
        return sum + item[ this.keyNamesForChartMainData[currentValue].listTotalItemCount];
      }, 0);

      return this.chartMainData[currentValue].map((item, index) => {
        const currentNamesForObject = this.keyNamesForChartMainData[currentValue];
        let listItemTitle = item[currentNamesForObject.listItemTitle];
        const listTotalItemCount = item[currentNamesForObject.listTotalItemCount];
        
        if (this.genderDistribution) {
          if (listItemTitle === 1) {
            listItemTitle = "Мужчины";
          } else  if (listItemTitle === 0) {
            listItemTitle = "Женщины";
          } else {
            listItemTitle = "Пол не указан";
          }
        }

        return {
          listItemTitle,
          listTotalItemCount,
          fillColor: CHART_DEFAULT_COLORS[index],
          persent: this.calculatePercentage(listTotalItemCount, totalCount),
          id: index + 1,
        };
      });
    },
    getOptions() {
      const labelList = this.getCurrentChartMainData.map((item, index) => {
        return item.listItemTitle;
      });
      return getDefaultDonutOptions(labelList,  this.getCurrentChartMainData);
    },
    getSeries() {
        return this.getCurrentChartMainData.map((item, index) => {
          return item.listTotalItemCount;
        });
    },
    getYearsRange() {
      const yearsRange = [];
      for (let year = 2017; year <= new Date().getFullYear(); year++ ) {
        yearsRange.unshift({value: year});
      }
      return yearsRange;
    },
    isHideShowAllBtn() {
      return this.getCurrentChartMainData.length > this.visibleCount && this.getCurrentChartMainData.length !== this.visibleCount;
    },
  },
  methods: {
    updateRadioButtonOptions(value) {
      this.currentRadioButtonValue = value;
    },
    calculatePercentage(part, total) {
      if (total === 0) return null;
      return ((part / total) * 100).toFixed(2);
    },
    toggleShowAll() {
      this.showAll = !this.showAll;
    },
  },
  created() {
    if (this.radioButtonOptions && this.radioButtonOptions.length) {
      this.currentRadioButtonValue = this.radioButtonOptions[0].value;
    }
  },
}; 
</script>
<style lang="scss">
@import '~@/styles/rem';
@import '~@/styles/chart_styles';
@import '~@/styles/variables';

.chart-template {
  &__wrapper {
    display: flex;
    gap: rem(40);
  }

  &__chart-wrapper {
    margin-top: rem(40);
  }

  &__show-all {
    display: flex;
    align-items: center;
    margin-top: rem(24);
    gap: rem(6);
    font-family: Montserrat, sans-serif;
    font-size: rem(14);
    font-weight: 500;
    color: #4663AB;
    cursor: pointer;
   
    &-icon {
      width: rem(24);
      height: rem(24);  
      background: url("~@/assets/images/icons/chart-show-all-arrow.svg") no-repeat center/contain;

      &_rotate {
        transform: rotate(180deg);
      }
    }
  }

  &__item {
    display: flex;
    align-items: center;
    gap: rem(40);
    font-family: Montserrat, sans-serif;
    font-weight: 600;
    font-size: rem(14);
    line-height: rem(24);
    padding-bottom: rem(4);
    margin-bottom: rem(6);
    border-bottom: rem(1) solid rgba(232, 232, 232, 1);

    &-circle {
      width: rem(12);
      height: rem(12);
      margin-bottom: rem(1);
      margin-right: rem(6);
      border-radius: 50%;
    }

    &-title {
      display: flex;
      align-items: center;
      width: rem(488);

      &-text {
        width: rem(470);
      }
    }

    &-count {
      width: rem(90);
    }

    &-percent {
      width: rem(50);
      color: #4663AB;
    }
  }
  &__list {
    width: rem(708);
    margin-top: rem(40);
  }

  &__stats-filter {
    margin-top: rem(40);
  }
}
@include chart-wrapper-box;

.donut-chart-percent {
  color: #4663AB;
}

</style>
